import { notify } from '@affine/component';
import {
  generateUrl,
  type UseSharingUrl,
} from '@affine/core/components/hooks/affine/use-share-url';
import { ServerService } from '@affine/core/modules/cloud';
import { EditorService } from '@affine/core/modules/editor';
import { PublicLinksService } from '@affine/core/modules/share-doc';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { copyLinkToBlockStdScopeClipboard } from '@affine/core/utils/clipboard';
import { UserFriendlyError } from '@affine/error';
import { I18n } from '@affine/i18n';
import {
  menu,
  type MenuOptions,
} from '@blocksuite/affine/components/context-menu';
import type { DatabaseBlockModel } from '@blocksuite/affine/model';
import { DeleteIcon, LinkIcon, ShareIcon } from '@blocksuite/icons/lit';
import type { FrameworkProvider } from '@toeverything/infra';

export function createDatabaseOptionsConfig(framework: FrameworkProvider) {
  preloadPublicLinks(framework);
  return {
    configure: (model: DatabaseBlockModel, options: MenuOptions) => {
      const items = options.items;

      items.splice(2, 0, createCopyLinkToBlockMenuItem(framework, model));

      return options;
    },
    copyLinkToView: (model: DatabaseBlockModel, viewId: string) => {
      copyLinkToDatabaseView(framework, model, viewId);
    },
    viewMenuItems: (model: DatabaseBlockModel, viewId: string) =>
      createPublicViewMenuItems(framework, model, viewId),
  };
}

/**
 * Load the public links of the workspace ahead of time, so the view tab
 * context menu knows right away whether a view already has one.
 */
function preloadPublicLinks(framework: FrameworkProvider) {
  try {
    const workspace = framework.get(WorkspaceService).workspace;
    if (workspace.flavour === 'local') return;
    framework.get(PublicLinksService).revalidateIfStale().catch(console.error);
  } catch {
    // not inside a workspace (e.g. a standalone editor): nothing to preload
  }
}

/**
 * "Public link to view" items of the view tab context menu.
 *
 * The link opens a read-only page with just this view: the server renders its
 * rows and visible columns, the rest of the doc stays private.
 */
function createPublicViewMenuItems(
  framework: FrameworkProvider,
  model: DatabaseBlockModel,
  viewId: string
) {
  const workspace = framework.get(WorkspaceService).workspace;
  // local workspaces have no server to publish to
  if (workspace.flavour === 'local') return [];

  const publicLinks = framework.get(PublicLinksService);
  // keep the list fresh so the next opening shows the right items
  publicLinks.revalidateIfStale().catch(console.error);

  const docId = model.store.id;
  const blockId = model.id;
  const existing = publicLinks.findViewLink(docId, blockId, viewId);

  const notifyError = (error: unknown) => {
    const friendlyError = UserFriendlyError.fromAny(error);
    // the server rejects publishing/revoking without Doc.Publish rights
    // (workspace member without "Can manage" access on the doc) — show a
    // localized hint instead of the raw English message with the doc id
    const noPermission =
      friendlyError.is('DOC_ACTION_DENIED') ||
      friendlyError.type === 'NO_PERMISSION';
    notify.error({
      title: noPermission
        ? I18n['com.affine.public-view.error.no-permission.title']()
        : I18n['com.affine.public-view.error.title'](),
      message: noPermission
        ? I18n['com.affine.public-view.error.no-permission']()
        : friendlyError.message,
    });
  };

  const copy = () => {
    const linkPromise = existing
      ? Promise.resolve(existing)
      : publicLinks.publishView(docId, blockId, viewId);
    linkPromise
      .then(link =>
        navigator.clipboard.writeText(publicLinks.viewLinkUrl(link.id))
      )
      .then(() => {
        notify.success({
          title: I18n['com.affine.public-view.copied.title'](),
          message: I18n['com.affine.public-view.copied.message'](),
        });
      })
      .catch(notifyError);
  };

  if (!existing) {
    return [
      menu.action({
        name: I18n['com.affine.public-view.menu.copy'](),
        prefix: ShareIcon(),
        select: copy,
      }),
    ];
  }

  return [
    menu.subMenu({
      name: I18n['com.affine.public-view.menu.copy'](),
      prefix: ShareIcon(),
      options: {
        items: [
          menu.action({
            name: I18n['com.affine.settings.workspace.public-links.copy'](),
            prefix: LinkIcon(),
            select: copy,
          }),
          menu.action({
            name: I18n['com.affine.public-view.menu.revoke'](),
            prefix: DeleteIcon(),
            class: { 'delete-item': true },
            select: () => {
              publicLinks
                .revokeView(existing.id)
                .then(() => {
                  notify.success({
                    title: I18n['com.affine.public-view.revoked.title'](),
                  });
                })
                .catch(notifyError);
            },
          }),
        ],
      },
    }),
  ];
}

function copyLinkToDatabaseView(
  framework: FrameworkProvider,
  model: DatabaseBlockModel,
  viewId: string
) {
  const serverService = framework.get(ServerService);
  const { editor } = framework.get(EditorService);
  const mode = editor.mode$.value;

  if (mode === 'edgeless') return;

  const workspaceId = editor.doc.workspace.id;
  const pageId = model.store.id;
  const options: UseSharingUrl = {
    workspaceId,
    pageId,
    mode,
    blockIds: [model.id],
    viewId,
  };

  const str = generateUrl({
    ...options,
    baseUrl: serverService.server.baseUrl,
  });
  if (!str) return;

  const page = editor.editorContainer$.value;

  copyLinkToBlockStdScopeClipboard(str, page?.host?.std.clipboard)
    .then(success => {
      if (!success) return;

      notify.success({ title: I18n['Copied link to clipboard']() });
    })
    .catch(console.error);
}

function createCopyLinkToBlockMenuItem(
  framework: FrameworkProvider,
  model: DatabaseBlockModel
) {
  return menu.action({
    name: I18n['com.affine.editor.copy-link-to-block'](),
    prefix: LinkIcon({ width: '20', height: '20' }),
    hide: () => {
      const { editor } = framework.get(EditorService);
      const mode = editor.mode$.value;
      return mode === 'edgeless';
    },
    select: () => {
      const serverService = framework.get(ServerService);
      const pageId = model.store.id;
      const { editor } = framework.get(EditorService);
      const mode = editor.mode$.value;

      if (mode === 'edgeless') return;

      const workspaceId = editor.doc.workspace.id;
      const options: UseSharingUrl = {
        workspaceId,
        pageId,
        mode,
        blockIds: [model.id],
      };

      const str = generateUrl({
        ...options,
        baseUrl: serverService.server.baseUrl,
      });
      if (!str) return;

      const page = editor.editorContainer$.value;

      copyLinkToBlockStdScopeClipboard(str, page?.host?.std.clipboard)
        .then(success => {
          if (!success) return;

          notify.success({ title: I18n['Copied link to clipboard']() });
        })
        .catch(console.error);
    },
  });
}
